import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, ArrowUpRight, LayoutDashboard, ShieldCheck, LogOut, User as UserIcon, Store, FileText } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from './ui/sheet';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';
import { NAV_LINKS } from '../data/mock';
import { useConnect } from '../context/ConnectContext';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import logo from '../assets/farmway-logo.png';

function Avatar({ user, size = 'h-9 w-9' }) {
  if (user?.picture) {
    return <img src={user.picture} alt={user.name} className={`${size} rounded-full object-cover ring-1 ring-farmway-gold/40`} referrerPolicy="no-referrer" />;
  }
  const initial = (user?.name || 'U').charAt(0).toUpperCase();
  return <span className={`${size} grid place-items-center rounded-full bg-farmway-gold font-display font-bold text-farmway-green`}>{initial}</span>;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const { openConnect } = useConnect();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? 'border-b border-white/10 bg-farmway-green/90 py-3 backdrop-blur-xl' : 'border-b border-transparent bg-transparent py-5'
      }`}
    >
      <div className="container-fw flex items-center justify-between">
        <Link to="/" className="group flex items-center gap-3">
          <img src={logo} alt="Farmway" className="h-10 w-10 rounded-xl object-cover ring-1 ring-farmway-leaf/30 transition-transform duration-300 group-hover:scale-105" />
          <span className="font-display text-xl font-extrabold tracking-[0.2em] text-farmway-cream">FARMWAY</span>
        </Link>

        <nav className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.path} to={l.path} className="group relative font-body text-sm font-medium">
              {({ isActive }) => (
                <span className={`relative transition-colors ${isActive ? 'text-farmway-gold' : 'text-farmway-cream/80 group-hover:text-farmway-cream'}`}>
                  {l.label}
                  <span className={`absolute -bottom-1.5 left-0 h-[2px] bg-farmway-gold transition-all duration-300 ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-full border border-white/15 py-1 pl-1 pr-3 outline-none transition-colors hover:border-farmway-gold/50">
                <Avatar user={user} size="h-8 w-8" />
                <span className="max-w-[120px] truncate text-sm text-farmway-cream">{user.name?.split(' ')[0]}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-white/10 bg-farmway-green-dark text-farmway-cream">
                <DropdownMenuLabel className="text-farmway-cream/60">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer focus:bg-white/10 focus:text-farmway-cream">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                {(user.role === 'admin' || user.status === 'onboarded') && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/marketplace')} className="cursor-pointer focus:bg-white/10 focus:text-farmway-cream">
                      <Store className="mr-2 h-4 w-4" /> Marketplace
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="header-reports-link" onClick={() => navigate('/reports')} className="cursor-pointer focus:bg-white/10 focus:text-farmway-cream">
                      <FileText className="mr-2 h-4 w-4" /> Reports
                    </DropdownMenuItem>
                  </>
                )}
                {user.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer focus:bg-white/10 focus:text-farmway-cream">
                    <ShieldCheck className="mr-2 h-4 w-4" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer focus:bg-white/10 focus:text-farmway-cream">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="flex items-center gap-1.5 font-body text-sm font-medium text-farmway-cream/80 transition-colors hover:text-farmway-cream">
              <UserIcon className="h-4 w-4" /> Sign in
            </Link>
          )}
          {!user && (
            <button onClick={openConnect} className="btn-gold">
              Connect <ArrowUpRight className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger aria-label="Open menu" className="rounded-full border border-white/15 p-2.5 text-farmway-cream">
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] border-l border-white/10 bg-farmway-green-dark text-farmway-cream">
              <div className="mb-8 flex items-center gap-3">
                <img src={logo} alt="Farmway" className="h-9 w-9 rounded-lg object-cover" />
                <span className="font-display text-lg font-extrabold tracking-[0.2em]">FARMWAY</span>
              </div>
              {user && (
                <div className="mb-6 flex items-center gap-3 rounded-xl border border-white/10 p-3">
                  <Avatar user={user} size="h-9 w-9" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{user.name}</p>
                    <p className="truncate text-xs text-farmway-cream/50">{user.email}</p>
                  </div>
                </div>
              )}
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.path}>
                    <NavLink to={l.path} className={({ isActive }) => `rounded-lg px-4 py-3 font-display text-sm font-semibold uppercase tracking-wider transition-colors ${isActive ? 'bg-white/5 text-farmway-gold' : 'text-farmway-cream/85 hover:bg-white/5'}`}>
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                {user ? (
                  <>
                    <SheetClose asChild>
                      <NavLink to="/dashboard" className="rounded-lg px-4 py-3 font-display text-sm font-semibold uppercase tracking-wider text-farmway-cream/85 hover:bg-white/5">Dashboard</NavLink>
                    </SheetClose>
                    {user.role === 'admin' && (
                      <SheetClose asChild>
                        <NavLink to="/admin" className="rounded-lg px-4 py-3 font-display text-sm font-semibold uppercase tracking-wider text-farmway-cream/85 hover:bg-white/5">Admin</NavLink>
                      </SheetClose>
                    )}
                    <SheetClose asChild>
                      <button onClick={handleLogout} className="rounded-lg px-4 py-3 text-left font-display text-sm font-semibold uppercase tracking-wider text-farmway-cream/85 hover:bg-white/5">Sign out</button>
                    </SheetClose>
                  </>
                ) : (
                  <SheetClose asChild>
                    <NavLink to="/login" className="rounded-lg px-4 py-3 font-display text-sm font-semibold uppercase tracking-wider text-farmway-cream/85 hover:bg-white/5">Sign in</NavLink>
                  </SheetClose>
                )}
              </nav>
              <SheetClose asChild>
                <button onClick={openConnect} className="btn-gold mt-6 w-full">Connect <ArrowUpRight className="h-4 w-4" /></button>
              </SheetClose>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
